import {
  Alert,
  Avatar,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { extractApiErrorMessage } from '@/api/client';
import { getStudent, isHiddenCommunity } from '@/api/students';
import {
  COMMUNITY_MEMBER_STATUS_LABELS,
  ROLE_COMMUNITY_LABELS,
  type CommunityMemberStatus,
  type StudentMembership,
} from '@/types/students';

const { Title, Text, Paragraph } = Typography;

interface Props {
  userId: number | null;
  open: boolean;
  onClose: () => void;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '—';
}

const STATUS_COLOR: Record<CommunityMemberStatus, string> = {
  PENDING: 'gold',
  APPROVED: 'green',
  CANCELLING: 'orange',
  CHURNED: 'default',
  BANNED: 'red',
  REJECTED: 'volcano',
};

function MembershipCard({ membership }: { membership: StudentMembership }) {
  return (
    <Card size="small" styles={{ body: { padding: 12 } }}>
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <Space size={6} wrap>
          <Text strong>
            {membership.communityName ?? `Community #${membership.communityId}`}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            ID #{membership.communityId}
          </Text>
        </Space>
        <Space size={4} wrap>
          {membership.membershipStatus ? (
            <Tag color={STATUS_COLOR[membership.membershipStatus]}>
              {COMMUNITY_MEMBER_STATUS_LABELS[membership.membershipStatus]}
            </Tag>
          ) : null}
          {membership.communityRole ? (
            <Tag>{ROLE_COMMUNITY_LABELS[membership.communityRole]}</Tag>
          ) : null}
          {membership.cohort ? <Tag color="cyan">K{membership.cohort}</Tag> : null}
        </Space>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Tham gia: {formatDateTime(membership.joinedAt)}
          {membership.approvedAt
            ? ` · Duyệt: ${formatDateTime(membership.approvedAt)}`
            : ''}
          {membership.lastActive
            ? ` · Hoạt động: ${formatDateTime(membership.lastActive)}`
            : ''}
        </Text>
      </Space>
    </Card>
  );
}

export function StudentDetailDrawer({ userId, open, onClose }: Props) {
  const query = useQuery({
    queryKey: ['student', userId],
    queryFn: () => getStudent(userId as number),
    enabled: open && userId !== null,
  });

  const detail = query.data;
  const errorMessage = query.error
    ? extractApiErrorMessage(query.error, 'Không tải được thông tin học viên.')
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      destroyOnClose
      title={
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Thông tin học viên
          </Text>
          <Title level={4} style={{ margin: 0 }}>
            {detail?.fullname ?? (query.isLoading ? 'Đang tải…' : '—')}
          </Title>
        </Space>
      }
    >
      {errorMessage ? (
        <Alert type="error" showIcon message="Lỗi" description={errorMessage} />
      ) : null}

      {query.isLoading ? (
        <Skeleton avatar paragraph={{ rows: 6 }} active />
      ) : detail ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space size="middle" align="start">
            <Avatar
              src={detail.avatarUrl ?? undefined}
              icon={<UserOutlined />}
              size={64}
            />
            <Space direction="vertical" size={4}>
              <Space wrap size={4}>
                {detail.userActive === false ? (
                  <Tag color="default">Đã khoá</Tag>
                ) : (
                  <Tag color="green">Hoạt động</Tag>
                )}
                {detail.userRole ? <Tag color="geekblue">{detail.userRole}</Tag> : null}
              </Space>
              <Text type="secondary">User ID #{detail.userId}</Text>
            </Space>
          </Space>

          <Descriptions
            column={1}
            size="small"
            bordered
            labelStyle={{ width: 160 }}
          >
            <Descriptions.Item label="Họ và tên">
              {detail.fullname ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {detail.email ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Số điện thoại">
              {detail.mobile ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Vị trí">
              {detail.locationName ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Bio">
              <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-line' }}>
                {detail.bio ?? '—'}
              </Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label="Tạo tài khoản">
              {formatDateTime(detail.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Cập nhật gần nhất">
              {formatDateTime(detail.updatedAt)}
            </Descriptions.Item>
          </Descriptions>

          {(() => {
            const visible = detail.memberships.filter(
              (m) => !isHiddenCommunity(m.communityId),
            );
            return (
              <div>
                <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                  Cộng đồng tham gia ({visible.length})
                </Title>
                {visible.length === 0 ? (
                  <Empty
                    description="Chưa thuộc cộng đồng nào (ngoài mặc định)"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {visible.map((m) => (
                      <MembershipCard key={m.membershipId} membership={m} />
                    ))}
                  </Space>
                )}
              </div>
            );
          })()}
        </Space>
      ) : null}
    </Drawer>
  );
}
